import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import OrderDetailsDialog from "@/components/dialog/CBD/Order-details-dialog";
import {
  Eye,
  FileText,
  Car,
  Globe,
  Funnel,
  BriefcaseBusiness,
  Phone,
  Mail,
  MapPin,
  User,
  PlusCircle,
  Copy,
  Check,
  Search,
  RotateCcw,
  Sparkles,
  ShoppingBag,
  Clock,
  Calendar as CalendarIcon,
  Utensils,
  Receipt,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Building2
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { blackUser } from "@/assets";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchCbdCustomerOrders,
  fetchSingleCbdOrder,
} from "@/redux/slice/cbd-slice/cbd-slice";
import { setSelectedCustomer } from "@/redux/slice/take-order-slice/selected-customer-slice";
import { setDeliveryInfo } from "@/redux/slice/take-order-slice/delivery-slice";
import useAuth from "@/hooks/useAuth";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import Paginat from "@/components/lib/paginat";
import useDebounce from "@/hooks/useDebounce";
import toast from "react-hot-toast";

function SingleCustomerDetails() {
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(null);
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateError, setDateError] = useState("");
  const [copiedField, setCopiedField] = useState(null);
  const [addressesModalOpen, setAddressesModalOpen] = useState(false);
  const [firstLoading, setFirstLoading] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [page, setPage] = useState(1);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();

  const {
    currentCustomer,
    customerOrders,
    customerStatistics,
    getLoading,
    ordersPagination,
    singleOrder,
    singleOrderLoading,
  } = useSelector((state) => state.cbd.cbd);

  useEffect(() => {
    setFirstLoading(true);
    if (id && user?.restaurant_id) {
      const params = {
        per_page: 10,
        page: page,
        sort_field: "created_at",
        sort_order: "desc",
        search_term: debouncedSearchTerm || undefined,
        order_type: orderTypeFilter !== "all" ? orderTypeFilter : undefined,
      };

      if (startDate) {
        params.start_date = format(startDate, "yyyy-MM-dd");
      }
      if (endDate) {
        params.end_date = format(endDate, "yyyy-MM-dd");
      }

      dispatch(
        fetchCbdCustomerOrders({
          customerId: id,
          restaurant_id: user.restaurant_id,
          params,
        })
      );
    }
    setFirstLoading(false);
  }, [
    id,
    dispatch,
    user?.restaurant_id,
    page,
    debouncedSearchTerm,
    orderTypeFilter,
    startDate,
    endDate,
  ]);

  // Handle from date selection
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(1);
    setDateError("");
    if (endDate && date && date > endDate) {
      setDateError("Start date cannot be after end date");
    }
  };

  // Handle to date selection
  const handleEndDateChange = (date) => {
    if (startDate && date && date < startDate) {
      setDateError("End date cannot be before start date");
      return;
    }
    setEndDate(date);
    setPage(1);
    setDateError("");
  };

  // Handle Copy to clipboard
  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`${fieldName} copied to clipboard!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Start Take Order with this customer preselected
  const handleTakeNewOrder = () => {
    if (!currentCustomer) return;
    const activeAddress =
      currentCustomer?.addresses?.find((a) => a.is_active) ||
      currentCustomer?.active_address ||
      null;

    const customerData = {
      id: currentCustomer?.id,
      full_name: currentCustomer?.customer_name || "",
      number: currentCustomer?.mobile_number || currentCustomer?.home_number || "",
      email: currentCustomer?.email || "",
      active_address: activeAddress,
    };

    dispatch(setSelectedCustomer(customerData));
    if (activeAddress) {
      dispatch(setDeliveryInfo(activeAddress));
    }
    toast.success(`Customer ${customerData.full_name} selected for new order`);
    navigate("/take-order");
  };

  const handleEyeClick = (orderId) => {
    if (orderId && user?.restaurant_id) {
      setOrderDetailsDialog(orderId);
      dispatch(fetchSingleCbdOrder(orderId));
    }
  };

  // Client-side filtered orders for instant payment status filtering
  const filteredOrders = useMemo(() => {
    if (!customerOrders) return [];
    return customerOrders.filter((order) => {
      if (
        paymentStatusFilter !== "all" &&
        order?.payment_status?.toLowerCase() !== paymentStatusFilter.toLowerCase()
      ) {
        return false;
      }
      return true;
    });
  }, [customerOrders, paymentStatusFilter]);

  // Calculate dynamic stats from backend statistics with fallbacks
  const stats = useMemo(() => {
    const totalOrders =
      customerStatistics?.total_orders !== undefined && customerStatistics?.total_orders !== null
        ? Number(customerStatistics.total_orders)
        : currentCustomer?.orders?.length ?? customerOrders?.length ?? 0;

    const inHouseOrders =
      customerStatistics?.in_house_orders !== undefined && customerStatistics?.in_house_orders !== null
        ? Number(customerStatistics.in_house_orders)
        : (customerOrders?.filter((o) => o?.order_type === "in_house").length || 0);

    const pickupOrders =
      customerStatistics?.pickup_orders !== undefined && customerStatistics?.pickup_orders !== null
        ? Number(customerStatistics.pickup_orders)
        : (customerOrders?.filter((o) => o?.order_type === "pickup").length || 0);

    const deliveryOrders =
      customerStatistics?.delivery_orders !== undefined && customerStatistics?.delivery_orders !== null
        ? Number(customerStatistics.delivery_orders)
        : (customerOrders?.filter((o) => o?.order_type === "delivery").length || 0);

    const onlineOrders =
      customerStatistics?.online_orders !== undefined && customerStatistics?.online_orders !== null
        ? Number(customerStatistics.online_orders)
        : (customerOrders?.filter((o) => o?.order_type === "online" || o?.source === "online").length || 0);

    const tableOrders =
      customerStatistics?.table_orders !== undefined && customerStatistics?.table_orders !== null
        ? Number(customerStatistics.table_orders)
        : (customerOrders?.filter((o) => o?.order_type === "table" || o?.order_type === "dine_in").length || 0);

    // Lifetime spend directly from backend statistics (e.g. statistics.total_spend: 66.87)
    const totalSpent =
      customerStatistics?.total_spend !== undefined && customerStatistics?.total_spend !== null
        ? Number(customerStatistics.total_spend)
        : (currentCustomer?.orders || customerOrders || []).reduce(
            (sum, ord) => sum + Number(ord?.amount || ord?.total_price || 0),
            0
          );

    // Total savings directly from backend statistics (e.g. statistics.total_savings: 2.07)
    const totalSavings =
      customerStatistics?.total_savings !== undefined && customerStatistics?.total_savings !== null
        ? Number(customerStatistics.total_savings)
        : (customerOrders || []).reduce(
            (sum, ord) =>
              sum +
              Number(
                ord?.total_savings ||
                  ord?.applied_spend_offer?.discount_amount ||
                  ord?.discount_amount ||
                  0
              ),
            0
          );

    // Avg order directly from backend statistics (e.g. statistics.avg_order: 22.29)
    const avgOrderValue =
      customerStatistics?.avg_order !== undefined && customerStatistics?.avg_order !== null
        ? Number(customerStatistics.avg_order)
        : totalOrders > 0
        ? totalSpent / totalOrders
        : 0;

    return {
      totalOrders,
      inHouseOrders,
      pickupOrders,
      deliveryOrders,
      onlineOrders,
      tableOrders,
      totalSpent,
      totalSavings,
      avgOrderValue,
    };
  }, [customerStatistics, currentCustomer, customerOrders]);

  // Find active and all addresses
  const customerAddresses = currentCustomer?.addresses || [];
  const activeAddress =
    customerAddresses.find((addr) => addr.is_active) ||
    currentCustomer?.active_address ||
    customerAddresses[0];

  // Helper for order type styling
  const renderOrderTypeBadge = (type) => {
    const lowerType = (type || "").toLowerCase().trim();
    switch (lowerType) {
      case "in_house":
      case "in-house":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shadow-sm">
            <Building2 className="w-3.5 h-3.5" />
            In House
          </span>
        );
      case "pickup":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <BriefcaseBusiness className="w-3.5 h-3.5" />
            Pickup
          </span>
        );
      case "delivery":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <Car className="w-3.5 h-3.5" />
            Delivery
          </span>
        );
      case "online":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Globe className="w-3.5 h-3.5" />
            Online
          </span>
        );
      case "table":
      case "dine_in":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-pink-500/10 text-pink-400 border border-pink-500/20">
            <Utensils className="w-3.5 h-3.5" />
            Table
          </span>
        );
      default: {
        const formatted = (type || "Standard")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-700/50 text-neutral-300 border border-neutral-600">
            <ShoppingBag className="w-3.5 h-3.5" />
            {formatted}
          </span>
        );
      }
    }
  };

  // Helper for payment status badge
  const renderPaymentStatusBadge = (status) => {
    const lower = (status || "").toLowerCase();
    if (lower === "paid") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          <CheckCircle2 className="w-3 h-3" />
          Paid
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <AlertCircle className="w-3 h-3" />
        Unpaid
      </span>
    );
  };

  // Helper for kitchen / stage status
  const renderKitchenStatus = (kitchenStatus, stage) => {
    if (!kitchenStatus && !stage) return <span className="text-neutral-500 text-xs">N/A</span>;
    const formatted = (kitchenStatus || stage || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    const isReady =
      kitchenStatus === "ready_for_pickup" ||
      kitchenStatus === "ready" ||
      stage === "completed";

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${
          isReady
            ? "bg-emerald-950/60 text-emerald-300 border border-emerald-800/40"
            : "bg-neutral-800 text-neutral-300 border border-neutral-700"
        }`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${
            isReady ? "bg-emerald-400 animate-pulse" : "bg-amber-400"
          }`}
        />
        {formatted}
      </span>
    );
  };

  return (
    <Layout2
      pageName={{ icon: true, title: `Customer Base Database (CBD)` }}
      paths={[
        { name: "CBD", path: "/cbd" },
        { name: "All Customer", path: "/cbd/all-customer" },
        {
          name: currentCustomer?.customer_name || "Single Customer Details",
          path: `/cbd/single-customer-details/${id}`,
        },
      ]}
    >
      <div className="w-full px-4 md:px-6 lg:px-8 py-5 space-y-5">
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* ================= LEFT COLUMN: CUSTOMER PROFILE ================= */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-4">
            <Card className="bg-[#1a1a1a] border-neutral-800 shadow-xl overflow-hidden rounded-xl">
              {/* Header Gradient Banner */}
              <div className="h-16 bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-transparent relative p-3 flex items-center justify-between border-b border-neutral-800/60">
                {currentCustomer?.is_banned ? (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                    Banned Customer
                  </Badge>
                ) : (
                  <span />
                )}
              </div>

              <CardContent className="p-5 pt-0 space-y-5">
                {/* Profile Header */}
                <div className="flex flex-col items-center text-center -mt-9 space-y-2">
                  <div className="relative">
                    <Avatar className="w-20 h-20 rounded-2xl ring-4 ring-[#1a1a1a] shadow-2xl border border-neutral-700 bg-[#222]">
                      <AvatarImage
                        className="h-full w-full object-cover"
                        src={blackUser}
                        alt={currentCustomer?.customer_name || "Customer"}
                      />
                      <AvatarFallback className="bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-bold text-xl">
                        {currentCustomer?.customer_name?.charAt(0) || "C"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-1 -right-1 bg-emerald-500 w-4 h-4 rounded-full border-2 border-[#1a1a1a]" />
                  </div>

                  <div className="space-y-1">
                    <h2 className="text-xl font-bold text-white tracking-tight">
                      {currentCustomer?.customer_name || "Customer Profile"}
                    </h2>
                    {/* ONLY UNIQUE ID */}
                    <div className="flex justify-center">
                      <span className="inline-block bg-neutral-900 border border-neutral-700/80 text-amber-400 font-mono text-xs px-2.5 py-0.5 rounded-md font-medium shadow-sm">
                        {currentCustomer?.unique_id || "CT-00000-000000"}
                      </span>
                    </div>
                  </div>

                  {/* Primary CTA: Take New Order */}
                  <Button
                    onClick={handleTakeNewOrder}
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold shadow-lg shadow-amber-500/10 transition-all duration-200 gap-2 h-10 mt-1"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Take New Order
                  </Button>
                </div>

                <Separator className="bg-neutral-800" />

                {/* Lifetime Spending Highlights */}
                <div className="grid grid-cols-2 gap-2 bg-[#222222] p-3 rounded-xl border border-neutral-800/80">
                  <div className="space-y-0.5">
                    <p className="text-[11px] text-neutral-400 uppercase tracking-wider font-medium">
                      Total Spend
                    </p>
                    <p className="text-base font-bold text-emerald-400">
                      {formatCurrency(stats.totalSpent)}
                    </p>
                  </div>
                  <div className="space-y-0.5 border-l border-neutral-800 pl-3">
                    <p className="text-[11px] text-neutral-400 uppercase tracking-wider font-medium">
                      Avg / Order
                    </p>
                    <p className="text-base font-bold text-amber-400">
                      {formatCurrency(stats.avgOrderValue)}
                    </p>
                  </div>
                  {stats.totalSavings > 0 && (
                    <div className="col-span-2 pt-2 border-t border-neutral-800/60 flex items-center justify-between text-xs">
                      <span className="text-neutral-400 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-amber-400" /> Total Savings
                      </span>
                      <span className="text-emerald-400 font-semibold">
                        {formatCurrency(stats.totalSavings)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                    Contact Details
                  </h3>

                  {/* Mobile Number */}
                  <div className="flex items-center justify-between bg-[#222222] p-2.5 rounded-lg border border-neutral-800 hover:border-neutral-700 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="p-1.5 rounded-md bg-neutral-800 text-amber-400">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div className="truncate">
                        <p className="text-[10px] text-neutral-400 font-medium leading-none">Mobile</p>
                        <p className="text-sm font-mono text-white font-medium truncate mt-0.5">
                          {currentCustomer?.mobile_number || "N/A"}
                        </p>
                      </div>
                    </div>
                    {currentCustomer?.mobile_number && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(currentCustomer.mobile_number, "Mobile Number")}
                        className="h-7 w-7 text-neutral-400 hover:text-white hover:bg-neutral-700/50"
                        title="Copy Mobile"
                      >
                        {copiedField === "Mobile Number" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Home Number */}
                  {currentCustomer?.home_number && (
                    <div className="flex items-center justify-between bg-[#222222] p-2.5 rounded-lg border border-neutral-800 hover:border-neutral-700 transition-colors">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-1.5 rounded-md bg-neutral-800 text-blue-400">
                          <Phone className="w-4 h-4" />
                        </div>
                        <div className="truncate">
                          <p className="text-[10px] text-neutral-400 font-medium leading-none">Home Phone</p>
                          <p className="text-sm font-mono text-white font-medium truncate mt-0.5">
                            {currentCustomer.home_number}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(currentCustomer.home_number, "Home Number")}
                        className="h-7 w-7 text-neutral-400 hover:text-white hover:bg-neutral-700/50"
                        title="Copy Home Phone"
                      >
                        {copiedField === "Home Number" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Email */}
                  <div className="flex items-center justify-between bg-[#222222] p-2.5 rounded-lg border border-neutral-800 hover:border-neutral-700 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="p-1.5 rounded-md bg-neutral-800 text-purple-400">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div className="truncate">
                        <p className="text-[10px] text-neutral-400 font-medium leading-none">Email</p>
                        <p className="text-sm text-white font-medium truncate mt-0.5">
                          {currentCustomer?.email || "No email provided"}
                        </p>
                      </div>
                    </div>
                    {currentCustomer?.email && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(currentCustomer.email, "Email")}
                        className="h-7 w-7 text-neutral-400 hover:text-white hover:bg-neutral-700/50"
                        title="Copy Email"
                      >
                        {copiedField === "Email" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <Separator className="bg-neutral-800" />

                {/* Addresses Section */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                      Primary Address
                    </h3>
                    {customerAddresses.length > 1 && (
                      <button
                        onClick={() => setAddressesModalOpen(true)}
                        className="text-xs text-amber-400 hover:text-amber-300 transition-colors font-medium flex items-center gap-1"
                      >
                        View All ({customerAddresses.length})
                      </button>
                    )}
                  </div>

                  <div className="bg-[#222222] p-3 rounded-lg border border-neutral-800 space-y-2">
                    <div className="flex items-start gap-2.5">
                      <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div className="text-xs text-neutral-300 space-y-1 leading-relaxed">
                        {activeAddress ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">
                                {activeAddress.door_no ? `Door ${activeAddress.door_no}` : "Address"}
                              </span>
                              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] py-0 px-1.5">
                                Active
                              </Badge>
                            </div>
                            <p className="text-neutral-300">{activeAddress.address}</p>
                            <p className="text-neutral-400 font-mono">
                              {[activeAddress.city, activeAddress.postal_code || activeAddress.post_code]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          </>
                        ) : (
                          <p className="text-neutral-400">
                            {currentCustomer?.address || "No saved address found"}
                          </p>
                        )}
                      </div>
                    </div>

                    {(activeAddress?.postal_code || currentCustomer?.post_code) && (
                      <div className="pt-2 border-t border-neutral-800/80 flex items-center justify-between text-xs">
                        <span className="text-neutral-400 font-mono">
                          Postcode:{" "}
                          <strong className="text-white">
                            {activeAddress?.postal_code || currentCustomer?.post_code}
                          </strong>
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleCopy(
                              activeAddress?.postal_code || currentCustomer?.post_code,
                              "Postcode"
                            )
                          }
                          className="h-6 px-2 text-[11px] text-amber-400 hover:text-amber-300 hover:bg-neutral-800"
                        >
                          Copy
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ================= RIGHT COLUMN: METRICS & ORDERS TABLE ================= */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-5">
            {/* Top Metric Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
              {/* Total Orders */}
              <Card className="bg-[#1a1a1a] border-neutral-800/80 hover:border-emerald-500/40 transition-all shadow-lg overflow-hidden group">
                <CardContent className="p-3.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-400 whitespace-nowrap">Total Orders</p>
                    <h3 className="text-xl xl:text-2xl font-bold text-white tracking-tight mt-1">
                      {stats.totalOrders}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <FileText className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              {/* In House Orders */}
              <Card className="bg-[#1a1a1a] border-neutral-800/80 hover:border-indigo-500/40 transition-all shadow-lg overflow-hidden group">
                <CardContent className="p-3.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-400 whitespace-nowrap">In House</p>
                    <h3 className="text-xl xl:text-2xl font-bold text-white tracking-tight mt-1">
                      {stats.inHouseOrders}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Building2 className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              {/* Pickup Orders */}
              <Card className="bg-[#1a1a1a] border-neutral-800/80 hover:border-blue-500/40 transition-all shadow-lg overflow-hidden group">
                <CardContent className="p-3.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-400 whitespace-nowrap">Pickup Orders</p>
                    <h3 className="text-xl xl:text-2xl font-bold text-white tracking-tight mt-1">
                      {stats.pickupOrders}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <BriefcaseBusiness className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Orders */}
              <Card className="bg-[#1a1a1a] border-neutral-800/80 hover:border-purple-500/40 transition-all shadow-lg overflow-hidden group">
                <CardContent className="p-3.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-400 whitespace-nowrap">Delivery Orders</p>
                    <h3 className="text-xl xl:text-2xl font-bold text-white tracking-tight mt-1">
                      {stats.deliveryOrders}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Car className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              {/* Online Orders */}
              <Card className="bg-[#1a1a1a] border-neutral-800/80 hover:border-amber-500/40 transition-all shadow-lg overflow-hidden group">
                <CardContent className="p-3.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-400 whitespace-nowrap">Online Orders</p>
                    <h3 className="text-xl xl:text-2xl font-bold text-white tracking-tight mt-1">
                      {stats.onlineOrders}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Globe className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              {/* Table Orders */}
              <Card className="bg-[#1a1a1a] border-neutral-800/80 hover:border-pink-500/40 transition-all shadow-lg overflow-hidden group">
                <CardContent className="p-3.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-neutral-400 whitespace-nowrap">Table Orders</p>
                    <h3 className="text-xl xl:text-2xl font-bold text-white tracking-tight mt-1">
                      {stats.tableOrders}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-pink-500/10 border border-pink-500/20 text-pink-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Utensils className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter and Search Bar with Date to Date Picker */}
            <Card className="bg-[#1a1a1a] border-neutral-800 shadow-md">
              <CardContent className="p-3.5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  {/* Filter by Order Type */}
                  <Select value={orderTypeFilter} onValueChange={(val) => setOrderTypeFilter(val)}>
                    <SelectTrigger className="w-[150px] bg-[#222] border-neutral-700 text-neutral-200 text-xs h-9">
                      <SelectValue placeholder="All Order Types" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#222] border-neutral-700 text-neutral-200">
                      <SelectItem value="all">All Order Types</SelectItem>
                      <SelectItem value="in_house">In House</SelectItem>
                      <SelectItem value="pickup">Pickup</SelectItem>
                      <SelectItem value="delivery">Delivery</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="table">Table</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Filter by Payment Status */}
                  <Select
                    value={paymentStatusFilter}
                    onValueChange={(val) => setPaymentStatusFilter(val)}
                  >
                    <SelectTrigger className="w-[140px] bg-[#222] border-neutral-700 text-neutral-200 text-xs h-9">
                      <SelectValue placeholder="Payment Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#222] border-neutral-700 text-neutral-200">
                      <SelectItem value="all">All Payments</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Date to Date Picker */}
                  <div className="flex items-center gap-1.5 bg-[#222] p-1 rounded-lg border border-neutral-700">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-7 px-2.5 text-xs text-neutral-200 hover:text-white hover:bg-neutral-800 flex items-center gap-1.5 font-normal"
                        >
                          <CalendarIcon className="w-3.5 h-3.5 text-amber-400" />
                          <span>{startDate ? format(startDate, "dd.MM.yyyy") : "dd.mm.yyyy"}</span>
                          <ChevronDown className="w-3 h-3 text-neutral-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#222] border-neutral-700 text-white" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={handleStartDateChange}
                        />
                      </PopoverContent>
                    </Popover>

                    <span className="text-neutral-500 text-xs font-semibold px-0.5">-</span>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-7 px-2.5 text-xs text-neutral-200 hover:text-white hover:bg-neutral-800 flex items-center gap-1.5 font-normal"
                        >
                          <CalendarIcon className="w-3.5 h-3.5 text-amber-400" />
                          <span>{endDate ? format(endDate, "dd.MM.yyyy") : "dd.mm.yyyy"}</span>
                          <ChevronDown className="w-3 h-3 text-neutral-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#222] border-neutral-700 text-white" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={handleEndDateChange}
                          disabled={(date) => startDate && date < startDate}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {(orderTypeFilter !== "all" ||
                    paymentStatusFilter !== "all" ||
                    searchTerm ||
                    startDate ||
                    endDate) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setOrderTypeFilter("all");
                        setPaymentStatusFilter("all");
                        setSearchTerm("");
                        setStartDate(null);
                        setEndDate(null);
                        setDateError("");
                      }}
                      className="h-9 px-2.5 text-xs text-neutral-400 hover:text-white hover:bg-neutral-800 gap-1"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Reset
                    </Button>
                  )}
                </div>

                {/* Search by Order ID / Keyword */}
                <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
                  <div className="relative w-full sm:w-[220px]">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by order id..."
                      className="bg-[#222] border-neutral-700 text-white placeholder:text-neutral-500 pl-9 pr-3 h-9 text-xs focus-visible:ring-amber-500/50"
                    />
                  </div>
                  <Button
                    size="icon"
                    className="h-9 w-9 bg-cyellow hover:bg-amber-500 text-black shrink-0"
                    title="Search"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
              {dateError && (
                <div className="px-4 pb-2 text-xs text-red-400 font-medium">
                  {dateError}
                </div>
              )}
            </Card>

            {/* Orders Table Card */}
            <Card className="bg-[#1a1a1a] border-neutral-800 shadow-xl overflow-hidden rounded-xl">
              <div className="p-4 border-b border-neutral-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-amber-400" />
                  <h3 className="font-semibold text-white text-sm">Order History</h3>
                  <Badge variant="outline" className="bg-neutral-800/80 text-neutral-300 text-xs ml-1 border-neutral-700">
                    {ordersPagination?.total ?? filteredOrders.length} records
                  </Badge>
                </div>
              </div>

              <div className="overflow-x-auto">
                {getLoading ? (
                  <div className="p-4">
                    <OnlyTableSekeleton row={5} header={false} />
                  </div>
                ) : (
                  <Table className="w-full">
                    <TableHeader className="bg-[#222] border-b border-neutral-800">
                      <TableRow className="hover:bg-transparent border-neutral-800">
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Order ID</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Date & Time</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Type</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Items</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Status</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Payment</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Total Amount</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Discount</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3">Operator</TableHead>
                        <TableHead className="text-neutral-400 text-xs font-semibold py-3 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-neutral-800/60 text-xs">
                      {filteredOrders && filteredOrders.length > 0 ? (
                        filteredOrders.map((order) => {
                          const orderSavings =
                            Number(order?.total_savings) ||
                            Number(order?.applied_spend_offer?.discount_amount) ||
                            Number(order?.discount_amount) ||
                            0;

                          const orderDate = order?.created_at
                            ? new Date(order.created_at)
                            : null;

                          return (
                            <TableRow
                              key={order?.id}
                              className="hover:bg-[#252525]/70 transition-colors border-neutral-800/60"
                            >
                              {/* Order ID: SHOW ONLY order.id (e.g. #33, #32, #31) */}
                              <TableCell className="py-3 font-medium">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-white font-bold text-sm">
                                    #{order?.id}
                                  </span>
                                  {order?.source && (
                                    <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-neutral-800 text-neutral-400 border border-neutral-700">
                                      {order.source}
                                    </span>
                                  )}
                                </div>
                              </TableCell>

                              {/* Date & Time */}
                              <TableCell className="py-3">
                                <div className="space-y-0.5">
                                  <p className="text-white font-medium">
                                    {orderDate
                                      ? orderDate.toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })
                                      : "N/A"}
                                  </p>
                                  <p className="text-neutral-400 flex items-center gap-1 text-[11px]">
                                    <Clock className="w-3 h-3 text-neutral-500" />
                                    {orderDate
                                      ? orderDate.toLocaleTimeString("en-GB", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          hour12: true,
                                        })
                                      : ""}
                                  </p>
                                </div>
                              </TableCell>

                              {/* Order Type */}
                              <TableCell className="py-3">
                                {renderOrderTypeBadge(order?.order_type)}
                              </TableCell>

                              {/* Items count */}
                              <TableCell className="py-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-neutral-800 text-neutral-300 font-medium">
                                  {order?.total_items || order?.items?.length || 1}{" "}
                                  {(order?.total_items || order?.items?.length || 1) === 1 ? "Item" : "Items"}
                                </span>
                              </TableCell>

                              {/* Kitchen Status & Stage */}
                              <TableCell className="py-3">
                                {renderKitchenStatus(order?.kitchen_status, order?.stage)}
                              </TableCell>

                              {/* Payment Status */}
                              <TableCell className="py-3">
                                {renderPaymentStatusBadge(order?.payment_status)}
                              </TableCell>

                              {/* Total Amount */}
                              <TableCell className="py-3 font-semibold text-white text-sm">
                                {formatCurrency(order?.total_price || order?.order_total || 0)}
                              </TableCell>

                              {/* Discount & Savings */}
                              <TableCell className="py-3">
                                {orderSavings > 0 ? (
                                  <div className="space-y-0.5">
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-semibold text-[11px] border border-emerald-500/20">
                                      -{formatCurrency(orderSavings)}
                                    </span>
                                    {order?.applied_spend_offer?.name && (
                                      <p className="text-[10px] text-neutral-400 truncate max-w-[100px]">
                                        {order.applied_spend_offer.name}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-neutral-500 font-mono text-xs">£0.00</span>
                                )}
                              </TableCell>

                              {/* Operator Name */}
                              <TableCell className="py-3">
                                <div className="flex items-center gap-1.5 text-neutral-300">
                                  <User className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                                  <span className="truncate max-w-[110px]" title={order?.operator || `Staff #${order?.staff_id}`}>
                                    {order?.operator || (order?.staff_id ? `Staff #${order.staff_id}` : "N/A")}
                                  </span>
                                </div>
                              </TableCell>

                              {/* Action View Details */}
                              <TableCell className="py-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEyeClick(order?.id)}
                                  className="h-8 w-8 rounded-lg bg-neutral-800/80 hover:bg-amber-500 hover:text-black text-neutral-300 transition-colors shadow-sm"
                                  title="View Order Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            className="text-center py-12 text-neutral-400"
                          >
                            <div className="flex flex-col items-center justify-center space-y-2">
                              <ShoppingBag className="w-8 h-8 text-neutral-600" />
                              <p className="font-medium text-neutral-300">No orders found</p>
                              <p className="text-xs text-neutral-500">
                                {searchTerm || orderTypeFilter !== "all" || paymentStatusFilter !== "all" || startDate || endDate
                                  ? "Try clearing filters or date range to see orders."
                                  : "This customer hasn't placed any orders yet."}
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Order Details Dialog */}
              <OrderDetailsDialog
                isOpen={!!orderDetailsDialog}
                onClose={() => setOrderDetailsDialog(null)}
                order={singleOrder}
                isLoading={singleOrderLoading}
                firstLoading={firstLoading}
              />

              {/* Pagination footer (ALWAYS SHOW WHEN PAGINATION DATA EXISTS) */}
              {ordersPagination && (
                <div className="p-3.5 border-t border-neutral-800 flex justify-end">
                  <Paginat
                    pagination={ordersPagination}
                    onPageChange={(p) => setPage(p)}
                  />
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* ================= ALL ADDRESSES MODAL ================= */}
      <Dialog open={addressesModalOpen} onOpenChange={setAddressesModalOpen}>
        <DialogContent className="max-w-md bg-[#1c1c1c] border-neutral-700 text-white p-5 rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Building2 className="w-5 h-5 text-amber-400" />
              Saved Addresses ({customerAddresses.length})
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-3 max-h-[60vh] overflow-y-auto pr-1">
            {customerAddresses.map((addr, idx) => (
              <div
                key={addr?.id || idx}
                className={`p-3.5 rounded-xl border transition-all ${
                  addr?.is_active
                    ? "bg-emerald-950/20 border-emerald-500/40 ring-1 ring-emerald-500/20"
                    : "bg-[#242424] border-neutral-800"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-white">
                        {addr?.door_no ? `Door No: ${addr.door_no}` : `Address #${idx + 1}`}
                      </span>
                      {addr?.is_active && (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] py-0 px-1.5">
                          Active Primary
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-neutral-300">{addr?.address}</p>
                    <p className="text-xs text-neutral-400 font-mono">
                      {[addr?.city, addr?.state, addr?.postal_code, addr?.country]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                  {addr?.postal_code && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCopy(addr.postal_code, "Postcode")}
                      className="h-7 w-7 text-neutral-400 hover:text-white hover:bg-neutral-700/50"
                      title="Copy Postcode"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout2>
  );
}

export default SingleCustomerDetails;
